import { useState } from 'react';

import { Label } from '@/components/shared/form/label';
import { keyframes, styled } from '@/stitches.config';
import { ITest, ITracking } from '@/types/tracking';

const wave = keyframes({
  '0%': {
    transform: 'scale(0)',
    opacity: '1',
  },
  to: {
    transform: 'scale(3)',
    opacity: '0',
  },
});

const RadioGroupItem = styled('div', {
  position: 'relative',
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  columnGap: '$2',
  cursor: 'pointer',
});

export const StyledRadio = styled('input', {
  all: 'unset',
  position: 'relative',
  size: 24,
  minWidth: 24,
  boxSizing: 'border-box',
  '&::before, &::after': {
    content: '""',
    display: 'block',
    position: 'absolute',
    br: '100%',
    zIndex: '1',
  },
  '&::before': {
    left: 2,
    top: 2,
    size: 20,
    bc: '$white',
    bs: 'inset 0px 0px 0px 2px $gray400',
  },
  '&::after': {
    left: 6,
    top: 6,
    size: 12,
    bc: '$primary',
    transform: 'scale(0)',
    '--transition-duration': '0.28s',
    transitionProperty: 'transform',
    transitionDelay: 'var(--transition-delay)',
    transitionDuration: 'var(--transition-duration)',
    transitionTimingFunction: 'var(--transition-easing)',
  },
  '& ~ span.radio-ink': {
    display: 'block',
    position: 'absolute',
    left: 0,
    top: 0,
    size: 24,
    bc: '$primary',
    br: '100%',
    pe: 'none',
    opacity: '0',
    transform: 'scale(0)',
  },
  '& ~ label': {
    position: 'relative',
    cursor: 'pointer',
    mb: '-2px',
    us: 'none',
  },
  '&:checked': {
    '&::before': {
      bs: 'inset 0px 0px 0px 2px $primary',
    },
    '&::after': {
      transform: 'scale(1)',
    },
    '& ~ span.radio-ink': {
      '--transition-duration': '1s',
      animationName: wave,
      animationDuration: 'var(--transition-duration)',
      animationTimingFunction: 'var(--transition-easing)',
      animationFillMode: 'backwards',
    },
  },
  '&:disabled': {
    '&::before': {
      bc: '$gray400',
    },
    '& ~ label > span': {
      color: '$gray400',
    },
  },
});

export const RadioGroup = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: '$4',
  rowGap: '$3',
  '&.remove-animation': {
    [`${StyledRadio}`]: {
      '& ~ span.radio-ink': {
        animation: 'none !important',
      },
    },
  },
  variants: {
    size: {
      1: {
        py: '$2',
      },
      2: {
        py: '$3',
      },
      3: {
        py: '$2',
        columnGap: '$6',
      },
    },
    disabled: {
      true: {
        pe: 'none',
      },
    },
  },
  defaultVariants: {
    size: 2,
  },
});

type IProps = React.ComponentProps<typeof RadioGroup> & {
  children?: React.ReactNode;
  items: {
    value: string;
    label?: string;
    test?: ITest;
    tracking?: ITracking;
    disabled?: false | boolean;
  }[];
  name: string;
  value?: string;
  size?: number;
  removeAnimation?: boolean;
  onUpdate: (name: string, value: string) => void;
};

export const Radio = (props: IProps) => {
  const [removeAnim, setRemoveAnim] = useState(true);

  const _onChange = (e) => {
    setRemoveAnim(false);
    props.onUpdate?.(props.name, e.target.value);
  };

  return (
    <RadioGroup
      size={props.size}
      disabled={props.disabled}
      className={removeAnim ? 'remove-animation' : ''}
    >
      {props.items.map((o, i) => {
        const inputId = `${props.name}_${i}`;
        return (
          <RadioGroupItem key={i}>
            <StyledRadio
              name={props.name}
              id={inputId}
              value={o.value}
              checked={o.value === props.value}
              type="radio"
              onChange={_onChange}
              data-track={o.tracking ? o.tracking?.dataTrack : ''}
              data-track-value={o.tracking ? o.tracking?.dataTrackValue : ''}
              data-track-text={o.tracking ? o.tracking?.dataTrackText : ''}
              data-track-url={o.tracking ? o.tracking?.dataTrackUrl : ''}
              data-test={o.test ? o.test.dataTest : ''}
              disabled={o.disabled}
            />
            <span className="radio-ink" />
            <Label for={inputId}>{o.label}</Label>
          </RadioGroupItem>
        );
      })}
    </RadioGroup>
  );
};
Radio.displayName = 'SharedFormRadio';
